import { z } from "zod";

import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";

import { AppPromotionSection, Footer, Header } from "./index";

export const Route = createFileRoute("/legal")({
	validateSearch: z.object({
		page: z.string().default("privacy")
	}),
	component: RouteComponent
});

const privacyPolicy = [
	{
		id: "introduction",
		title: "1. INTRODUCTION",
		content: (
			<p>
				Intasida Limited ("Intasida," "Company," "we," "us," or "our") is
				committed to protecting the rights and freedoms of data subjects
				(customers, employees, and partners) and ensuring the safe and secure
				processing of their personal data in compliance with the Nigeria Data
				Protection Act 2023, the Central Bank of Nigeria (CBN) Guidelines on
				Data Protection for Financial Institutions, and other applicable
				financial regulatory frameworks.
				<br />
				<br />
				We process personal data to provide secure and efficient financial
				services, ensure regulatory compliance, prevent fraud, and enhance user
				experience. This policy outlines our data protection commitments and
				ensures our employees and partners understand the principles governing
				the use of personal data.
			</p>
		)
	},
	{
		id: "definitions",
		title: "2. DEFINITIONS",
		content: (
			<ul className="list-disc pl-5 space-y-1">
				<li>
					<strong>Personal Data:</strong>
					{" "}
					Any information that identifies an
					individual, including name, phone number, email, financial details,
					account numbers, and transaction history.
				</li>
				<li>
					<strong>Special Category Data:</strong>
					{" "}
					Sensitive data including
					biometric data, health information, and criminal records, which
					require enhanced protection.
				</li>
				<li>
					<strong>Data Controller:</strong>
					{" "}
					Intasida Limited, determining the
					purpose and means of data processing.
				</li>
				<li>
					<strong>Data Processor:</strong>
					{" "}
					Any third party processing personal
					data on behalf of Intasida.
				</li>
				<li>
					<strong>Processing:</strong>
					{" "}
					Any operation performed on personal data,
					including collection, storage, transmission, and deletion.
				</li>
				<li>
					<strong>Supervisory Authority:</strong>
					{" "}
					The National Data Protection
					Commission (NDPC) and other relevant financial regulatory bodies.
				</li>
			</ul>
		)
	},
	{
		id: "scope",
		title: "3. SCOPE",
		content: (
			<p>
				This policy applies to all personal data processed by Intasida Limited,
				including data collected from customers, employees, vendors, and
				third-party service providers.
			</p>
		)
	},
	{
		id: "purpose",
		title: "4. PURPOSE",
		content: (
			<div>
				<p>Intasida processes personal data for:</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>Marketing and customer engagement (with consent)</li>
					<li>Financial transactions, including payments and loan services</li>
					<li>
						Fraud detection and compliance with anti-money laundering (AML)
						regulations
					</li>
					<li>Customer support and account management</li>
					<li>Internal business operations and human resources management</li>
				</ul>
			</div>
		)
	},
	{
		id: "procedures",
		title: "5. OUR PROCEDURES",
		content: (
			<div>
				<h3 className="text-lg font-medium mt-2 mb-1">
					5.1 Lawful Bases for Processing
				</h3>
				<p>
					Processing of personal data shall be lawful, fair, and transparent. We
					rely on the following lawful bases:
				</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong>Consent:</strong>
						{" "}
						Where customers explicitly agree to data
						collection and processing.
					</li>
					<li>
						<strong>Contractual Necessity:</strong>
						{" "}
						When processing is essential
						to fulfill a financial service.
					</li>
					<li>
						<strong>Legal Obligation:</strong>
						{" "}
						To comply with regulatory
						requirements.
					</li>
					<li>
						<strong>Legitimate Interest:</strong>
						{" "}
						When processing is necessary
						for fraud prevention, security, and business operations.
					</li>
				</ul>

				<h3 className="text-lg font-medium mt-4 mb-1">5.2 Security Measures</h3>
				<p>Intasida implements robust security measures, including:</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>Encryption of sensitive financial data.</li>
					<li>Multi-factor authentication (MFA) for customer accounts.</li>
					<li>Regular security audits and risk assessments.</li>
					<li>Employee training on data security and fraud prevention.</li>
				</ul>

				<h3 className="text-lg font-medium mt-4 mb-1">
					5.3 Data Minimization & Retention
				</h3>
				<p>
					We collect only necessary data and retain personal information for the
					duration required by law. Customer financial records are retained in
					line with the Nigerian Data Protection Act 2023 and other relevant
					regulations.
				</p>
			</div>
		)
	},
	{
		id: "responsibilities",
		title: "6. RESPONSIBILITIES OF INTASIDA",
		content: (
			<div>
				<p>Intasida shall ensure that personal data is:</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>Processed in a fair, lawful and transparent manner.</li>
					<li>
						Collected for specified, explicit, and legitimate purposes.
					</li>
					<li>Adequate, relevant, and limited to what is necessary.</li>
					<li>Accurate and kept up to date.</li>
					<li>
						Retained only for as long as necessary to fulfill legal and business
						obligations.
					</li>
				</ul>
			</div>
		)
	},
	{
		id: "rights",
		title: "7. RIGHTS OF INDIVIDUALS",
		content: (
			<div>
				<p>
					Individuals have the right to access, rectify, or erase their
					personal data. To exercise these rights, contact our Data Protection
					Officer at [email protected]
				</p>
			</div>
		)
	},
	{
		id: "third-party",
		title: "8. THIRD PARTY PROCESSING",
		content: (
			<p>
				We may share data with trusted third-party partners for payment
				processing, identity verification, and other essential services. All
				third parties are contractually bound to protect data in line with our
				standards.
			</p>
		)
	},
	{
		id: "breaches",
		title: "9. REPORTING BREACHES",
		content: (
			<p>
				In the event of a data breach, Intasida will notify the supervisory
				authority and affected individuals within 72 hours, in line with
				regulatory requirements.
			</p>
		)
	},
	{
		id: "audits",
		title: "10. COMPLIANCE & AUDITS",
		content: (
			<p>
				Intasida is subject to periodic internal and external audits to ensure
				continuous compliance with data protection regulations. We are committed
				to upholding the highest standards of data privacy and security.
			</p>
		)
	}
];

const termsAndConditions = [
	{
		id: "introduction",
		title: "1. INTRODUCTION",
		content: (
			<p>
				Welcome to Intasida, a digital platform that connects online vendors and
				buyers, facilitating seamless transactions between both parties. By
				accessing and using our website, you agree to be bound by these Terms
				and Conditions. If you do not agree with any part of these Terms, please
				discontinue use of our services immediately.
			</p>
		)
	},
	{
		id: "user-agreement",
		title: "2. USER AGREEMENT",
		content: (
			<p>
				By using this website, you confirm that you have read, understood, and
				agreed to abide by these Terms and Conditions. You also consent to the
				collection, processing, and storage of personal data provided on the
				platform in accordance with our Privacy Policy.
			</p>
		)
	},
	{
		id: "personal-data",
		title: "3. PERSONAL DATA PROCESSING",
		content: (
			<p>
				By using Intasida, you acknowledge and consent to the collection and
				processing of your personal data, including but not limited to your
				name, contact details, and transaction history. This information will be
				used to facilitate transactions, enhance user experience, and comply
				with regulatory requirements. For more details on how we handle your
				personal data, please refer to our Privacy Policy.
			</p>
		)
	},
	{
		id: "role-of-intasida",
		title: "4. ROLE OF INTASIDA",
		content: (
			<p>
				Intasida acts as an intermediary between online vendors and buyers. We
				do not own, manufacture, or directly sell any goods listed on our
				platform. Any disputes concerning products purchased must be resolved
				between the vendor and the buyer, subject to our dispute resolution
				process.
			</p>
		)
	}
];

function RouteComponent() {
	const { page } = useSearch({ from: "/legal" });

	return (
		<div className="bg-white">
			<div className="container mx-auto">
				<Header />
			</div>
			<main>
				<HeroSection />
				{page === "privacy" ? <PrivacyPolicySection /> : <TermsSection />}
				<div className="container mx-auto">
					<NewsletterSection />
					<AppPromotionSection />
				</div>
			</main>
			<Footer />
		</div>
	);
}

function HeroSection() {
	const { page } = useSearch({ from: "/legal" });

	return (
		<section
			className="relative bg-cover bg-center py-32"
			style={{ backgroundImage: "url(/legal.jpg)" }}
		>
			<div className="absolute inset-0 bg-black/50" />
			<div className="container mx-auto px-6 flex flex-col justify-end mt-8  relative">
				<h1 className="text-5xl font-bold text-white">Legal</h1>
				<p className="text-white/90 mt-4 max-w-2xl text-sm">
					We're committed to transparency and compliance with applicable laws and
					regulations.
				</p>
				<div className="mt-8 flex gap-4">
					<Button
						size="lg"
						asChild
						className={page === "privacy" ? "bg-primary text-white" : "bg-transparent border text-white hover:bg-white hover:text-primary"}
					>
						<Link to="/legal" search={{ page: "privacy" }}>
							Privacy Policy
						</Link>
					</Button>
					<Button
						size="lg"
						variant="outline"
						className={page === "terms" ? "bg-primary text-white border-primary" : "bg-transparent text-white hover:bg-white hover:text-primary"}
						asChild
					>
						<Link to="/legal" search={{ page: "terms" }}>
							Terms & Conditions
						</Link>
					</Button>
				</div>
			</div>
		</section>
	);
}

function PrivacyPolicySection() {
	return (
		<section className="py-20 bg-gray-50">
			<div className="container mx-auto px-6">
				<div className="flex justify-between items-center mb-12 max-md:flex-col max-md:items-start">
					<h2 className="text-3xl font-bold text-primary max-md:text-xl max-md:mb-4">
						PRIVACY POLICY
					</h2>
					<p className="text-gray-500 max-md:text-xs">Last Updated: April 2025</p>
				</div>
				<Accordion type="single" collapsible className="w-full space-y-4">
					{privacyPolicy.map(item => (
						<AccordionItem
							value={item.id}
							key={item.id}
							className="bg-white border rounded-lg"
						>
							<AccordionTrigger className="text-lg  px-2 max-md:text-sm font-medium text-primary text-left hover:no-underline">
								{item.title}
							</AccordionTrigger>
							<AccordionContent className="p-6 pt-0 text-gray-600">
								{item.content}
							</AccordionContent>
						</AccordionItem>
					))}
				</Accordion>
			</div>
		</section>
	);
}

function TermsSection() {
	return (
		<section className="py-20 bg-gray-50">
			<div className="container mx-auto px-6">
				<div className="flex justify-between items-center mb-12 max-md:flex-col max-md:items-start">
					<h2 className="text-3xl font-bold text-primary max-md:text-xl max-md:mb-4">
						TERMS AND CONDITIONS
					</h2>
					<p className="text-gray-500 max-md:text-xs">Last Updated: April 2025</p>
				</div>
				<Accordion type="single" collapsible className="w-full space-y-4">
					{termsAndConditions.map(item => (
						<AccordionItem
							value={item.id}
							key={item.id}
							className="bg-white border rounded-xl"
						>
							<AccordionTrigger className="text-lg  px-2 max-md:text-sm font-medium text-primary text-left hover:no-underline">
								{item.title}
							</AccordionTrigger>
							<AccordionContent className="p-4 text-gray-600">
								{item.content}
							</AccordionContent>
						</AccordionItem>
					))}
				</Accordion>
			</div>
		</section>
	);
}

function NewsletterSection() {
	return (
		<section className="bg-gray-50 py-10">
			<div className="container mx-auto px-6 text-center max-w-2xl">
				<h2 className="text-3xl font-bold text-primary-dark">
					Keep up with the latest
				</h2>
				<p className="text-gray-600 mt-2">
					Join our newsletter to stay up to date on features and releases.
				</p>
				<form className="mt-8 flex gap-2">
					<Input
						type="email"
						placeholder="Enter your email"
						className="flex-grow"
					/>
					<Button type="submit">Subscribe</Button>
				</form>
				<p className="text-sm text-gray-500 mt-4">
					To learn more about how Intasida handles your personal data, check our
					{" "}
					<Link to="/privacy" className="text-primary underline">
						Privacy Policy
					</Link>
					.
				</p>
			</div>
		</section>
	);
}
